"use client";

import type { PanchangResponse, PanchangPlanet } from "@/lib/api/panchang";
import type { CityData } from "@/lib/cities-data.types";
import { PANCHANG_CALCULATION_COPY } from "./Panchang.copy";
import { NakshatraLabel, PlanetLabel, RashiLabel, RashiPositionLabel } from "./Panchang.labels";

export const RASHI_HINDI: Record<string, string> = {
  mesh: "मेष",
  vrishabh: "वृषभ",
  mithun: "मिथुन",
  kark: "कर्क",
  singh: "सिंह",
  kanya: "कन्या",
  tula: "तुला",
  vrishchik: "वृश्चिक",
  dhanu: "धनु",
  makar: "मकर",
  kumbh: "कुंभ",
  meen: "मीन",
};

export const PLANET_LABELS: Record<string, { en: string; hi: string; symbol: string }> = {
  sun: { en: "Sun", hi: "सूर्य", symbol: "☉" },
  moon: { en: "Moon", hi: "चंद्र", symbol: "☽" },
  mars: { en: "Mars", hi: "मंगल", symbol: "♂" },
  mercury: { en: "Mercury", hi: "बुध", symbol: "☿" },
  jupiter: { en: "Jupiter", hi: "गुरु", symbol: "♃" },
  venus: { en: "Venus", hi: "शुक्र", symbol: "♀" },
  saturn: { en: "Saturn", hi: "शनि", symbol: "♄" },
  rahu: { en: "Rahu", hi: "राहु", symbol: "☊" },
  ketu: { en: "Ketu", hi: "केतु", symbol: "☋" },
};

export function NativeDisclosure({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details className="group" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg border border-ish-border bg-ish-bg-card px-4 py-3 text-body font-semibold text-ish-dark">
        {title}
        <span className="text-[18px] leading-none text-intent-brand transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

function SkeletonBlock({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-md bg-stone-200/70 ${className}`} aria-hidden="true" />
  );
}

export function PanchangResultSkeleton() {
  return (
    <div className="space-y-6" aria-label="Loading today's panchang">
      <div className="card-sacred p-6 sm:p-8">
        <div className="mb-6 text-center sm:text-left">
          <span className="block font-serif text-[32px] font-bold leading-tight text-ish-dark sm:text-[40px]">
            Today&apos;s Panchang
          </span>
          <span className="mt-2 block text-body text-ish-muted">
            Calculating today&apos;s tithi, nakshatra and muhurat
          </span>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {[0, 1].map((item) => (
            <div key={item} className="text-center sm:text-left">
              <SkeletonBlock className="mx-auto mb-3 h-3 w-20 sm:mx-0" />
              <SkeletonBlock className="mx-auto h-8 w-36 sm:mx-0" />
              <SkeletonBlock className="mx-auto mt-2 h-3 w-44 sm:mx-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="card-sacred p-4 text-center">
            <SkeletonBlock className="mx-auto h-3 w-20" />
            <SkeletonBlock className="mx-auto mt-3 h-5 w-16" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[0, 1].map((item) => (
          <div key={item} className="card-sacred p-4 text-center">
            <SkeletonBlock className="mx-auto h-3 w-16" />
            <SkeletonBlock className="mx-auto mt-3 h-5 w-24" />
          </div>
        ))}
      </div>

      <div className="card-sacred p-6 sm:p-8">
        <SkeletonBlock className="mb-5 h-6 w-44" />
        <div className="space-y-2">
          {[0, 1, 2, 3].map((item) => (
            <SkeletonBlock key={item} className="h-12" />
          ))}
        </div>
      </div>

      <div>
        <SkeletonBlock className="h-12 w-full" />
        <div className="card-sacred mt-3 space-y-2 p-6 sm:p-8">
          {[0, 1, 2, 3, 4, 5, 6].map((item) => (
            <SkeletonBlock key={item} className="h-14" />
          ))}
        </div>
      </div>

      <SkeletonBlock className="mx-auto h-11 w-52" />
    </div>
  );
}

// ---------------------------------------------------------------------------------

export function TithiNakshatraCard({ result }: { result: PanchangResponse }) {
  return (
    <div className="card-sacred p-6 sm:p-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <dl className="text-center sm:text-left">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-ish-faint">
            Tithi
          </dt>
          <dd>
            <span className="block text-[22px] font-bold text-ish-dark font-serif">
              {result.tithi.name}
            </span>
            <span className="block text-body-small mt-1 text-ish-muted">
              {result.tithi.paksha} Paksha &middot; Tithi {result.tithi.number}
            </span>
            {result.tithi.end_time ? (
              <span className="block text-caption mt-0.5 text-ish-faint">
                upto {result.tithi.end_time}
              </span>
            ) : null}
          </dd>
        </dl>
        <dl className="text-center sm:text-left">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-ish-faint">
            Nakshatra
          </dt>
          <dd>
            <span className="block">
              <NakshatraLabel nakshatraId={result.moon_nakshatra} />
            </span>
            <span className="block text-body-small mt-1 capitalize text-ish-muted">
              Moon in{" "}
              <RashiLabel
                rashiId={result.moon_rashi}
                className="text-body-small capitalize text-ish-muted font-medium"
              />{" "}
              ({RASHI_HINDI[result.moon_rashi] ?? ""})
            </span>
            {result.nakshatra.end_time ? (
              <span className="block text-caption mt-0.5 text-ish-faint">
                upto {result.nakshatra.end_time}
              </span>
            ) : null}
          </dd>
        </dl>
      </div>
    </div>
  );
}

export function QuickInfoGrid({ result }: { result: PanchangResponse }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Moon Rashi
        </dt>
        <dd>
          <RashiLabel rashiId={result.moon_rashi} />
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Sun Rashi
        </dt>
        <dd>
          <RashiLabel rashiId={result.sun_rashi} />
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Lagna
        </dt>
        <dd>
          <RashiLabel rashiId={result.lagna_rashi} />
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Paksha
        </dt>
        <dd className="text-body font-bold capitalize text-ish-dark">{result.tithi.paksha}</dd>
      </dl>
    </div>
  );
}

export function YogaKaranaRow({ result }: { result: PanchangResponse }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Yoga
        </dt>
        <dd>
          <span className="text-body font-bold text-ish-dark">{result.yoga.name}</span>
          {result.yoga.end_time ? (
            <span className="block text-[11px] mt-0.5 text-ish-faint">
              upto {result.yoga.end_time}
            </span>
          ) : null}
        </dd>
      </dl>
      <dl className="card-sacred p-4 text-center">
        <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
          Karana
        </dt>
        <dd className="text-body font-bold text-ish-dark">{result.karana.name}</dd>
      </dl>
    </div>
  );
}

export function SunMoonTimingsCard({ result }: { result: PanchangResponse }) {
  const items: ReadonlyArray<readonly [string, string | null | undefined]> = [
    ["Sunrise", result.sunrise],
    ["Sunset", result.sunset],
    ["Moonrise", result.moonrise],
    ["Moonset", result.moonset],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map(([label, value]) => (
        <dl key={label} className="card-sacred p-4 text-center">
          <dt className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
            {label}
          </dt>
          <dd className="text-body font-bold text-ish-dark">{value ?? "—"}</dd>
        </dl>
      ))}
    </div>
  );
}

export function PanchangCalculationDisclosure({
  result,
  city,
  isHindi,
}: {
  result: Pick<PanchangResponse, "date" | "location">;
  city: CityData;
  isHindi: boolean;
}) {
  const copy = PANCHANG_CALCULATION_COPY[isHindi ? "hi" : "en"];
  const facts = [
    [copy.place, isHindi ? (city.name_hi ?? city.name) : city.name],
    [copy.date, result.date],
    [copy.timezone, city.timezone],
    [copy.coordinates, `${result.location.latitude}, ${result.location.longitude}`],
  ];
  const explanations = [
    [copy.tithi, copy.tithiExplanation],
    [copy.nakshatra, copy.nakshatraExplanation],
    [copy.method, copy.methodExplanation],
    [copy.tradition, copy.traditionExplanation],
    [copy.limits, copy.limitsExplanation],
  ];

  return (
    <NativeDisclosure title={copy.title}>
      <div className="card-sacred space-y-5 p-5 sm:p-6 text-body-small text-ish-muted">
        <dl className="space-y-3">
          {facts.map(([label, value]) => (
            <div key={label} className="grid gap-1 sm:grid-cols-2 sm:gap-4">
              <dt>{label}</dt>
              <dd className="break-words font-semibold text-ish-dark">{value}</dd>
            </div>
          ))}
        </dl>
        <p>{copy.basis}</p>
        <p>{copy.locationExplanation}</p>
        {explanations.map(([title, explanation]) => (
          <div key={title}>
            <h2 className="mb-1 font-serif text-body font-semibold text-ish-dark">{title}</h2>
            <p>{explanation}</p>
          </div>
        ))}
        <a
          href="https://www.astro.com/swisseph/swephprg.htm"
          className="inline-block py-2 font-semibold text-intent-brand underline underline-offset-4"
        >
          {copy.reference}
        </a>
      </div>
    </NativeDisclosure>
  );
}

export function InauspiciousPeriodsCard({ result }: { result: PanchangResponse }) {
  const periods = [
    { label: "Rahu Kaal", period: result.rahu_kaal },
    { label: "Yamaghanta", period: result.yamaghanta },
    { label: "Gulika Kaal", period: result.gulika_kaal },
  ];
  return (
    <div className="card-sacred p-6 sm:p-8">
      <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">
        Auspicious &amp; Inauspicious Periods
      </h2>
      <dl className="space-y-2">
        {periods.map(({ label, period }) => (
          <div
            key={label}
            className="flex items-center justify-between p-3 rounded-lg bg-error/4 border border-error/12"
          >
            <dt className="text-body font-semibold text-ish-dark">{label}</dt>
            <dd className="text-body-small text-ish-muted">
              {period.start} – {period.end}
            </dd>
          </div>
        ))}
        {result.brahma_muhurat ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
            <dt className="text-body font-semibold text-ish-dark">Brahma Muhurat ✓</dt>
            <dd className="text-body-small text-ish-muted">
              {result.brahma_muhurat.start} – {result.brahma_muhurat.end}
            </dd>
          </div>
        ) : null}
        {result.amrit_kaal ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
            <dt className="text-body font-semibold text-ish-dark">Amrit Kaal ✓</dt>
            <dd className="text-body-small text-ish-muted">
              {result.amrit_kaal.start} – {result.amrit_kaal.end}
            </dd>
          </div>
        ) : null}
        <div className="flex items-center justify-between p-3 rounded-lg bg-success/4 border border-success/15">
          <dt className="text-body font-semibold text-ish-dark">Abhijit Muhurta ✓</dt>
          <dd className="text-body-small text-ish-muted">
            {result.abhijit_muhurta.start} – {result.abhijit_muhurta.end}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function PlanetRow({ planet }: { planet: PanchangPlanet }) {
  const label = PLANET_LABELS[planet.planet_id];
  if (!label) return null;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-black/2 border border-ish-border-light">
      <PlanetLabel
        planetId={planet.planet_id}
        symbol={label.symbol}
        labelEn={label.en}
        labelHi={label.hi}
      />
      <div className="text-right">
        <RashiPositionLabel rashiId={planet.rashi} />
        <span className="text-caption ml-1.5 text-ish-faint">
          {planet.rashi_degree.toFixed(1)}&deg;
        </span>
        {planet.is_retrograde && (
          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-error/10 text-error">
            R
          </span>
        )}
      </div>
    </div>
  );
}

export function PlanetPositionsDisclosure({ result }: { result: PanchangResponse }) {
  return (
    <NativeDisclosure title="Planetary Positions" defaultOpen>
      <div className="card-sacred p-6 sm:p-8 mt-0 space-y-2">
        {result.planets
          .filter((p) => PLANET_LABELS[p.planet_id])
          .map((planet: PanchangPlanet) => (
            <PlanetRow key={planet.planet_id} planet={planet} />
          ))}
      </div>
    </NativeDisclosure>
  );
}
